<template>
  <tr v-if='editMode || editLine'>
    <td>
      <v-select
        :items='$store.state.misc.divisions'
        item-value='id'
        item-text='division'
        v-model='lineCopy.division'
        append-outer-icon='mdi-close'
        @click:append-outer='lineCopy.division = null'
        hide-details
        single-line
        dense
        class='body-2'
        ref='div'
      ></v-select>
    </td>
    <td>
      <v-text-field
        ref='description_line'
        v-model='lineCopy.description'
        class='body-2'
        dense
        single-line
        hide-details
      ></v-text-field>
    </td>
    <td>
      <v-form v-model='isFormValid' ref='form'>
        <v-autocomplete
          ref='account'
          :items='accounts'
          item-value='id'
          item-text='name_number'
          class='body-2'
          single-line
          v-model='lineCopy.account'
          :rules='[rules.required]'
          dense
          hide-details
        >
          <template v-slot:item='data'>
            <account-picker-list-item :account='data.item'></account-picker-list-item>
          </template>
        </v-autocomplete>
      </v-form>
    </td>
    <td>
      <v-form v-model='isAmountValid' ref='form'>
        <v-text-field
          v-model='lineCopy.amount'
          :rules='[rules.decimal]'
          class='body-2'
          reverse
          dense
          single-line
          hide-details
        ></v-text-field>
      </v-form>
    </td>
    <td>
      <v-form v-model='isGSTValid' ref='form'>
        <v-text-field
          v-model='lineCopy.gst'
          :rules='[rules.decimalOrEmpty]'
          class='body-2'
          reverse
          dense
          single-line
          hide-details
          @focus='setInitialGST'
        ></v-text-field>
      </v-form>
    </td>
    <td class='text-right'>{{ line_total | dec }}</td>
    <td valign='middle' align='middle'>
      <v-btn
        x-small
        :disabled='savingLine'
        color='error'
        dark
        class='mr-1'
        @click='cancel'
      >
        <v-icon small>mdi-close</v-icon>
      </v-btn>
      <template v-if='isFormValid && isGSTValid && isAmountValid'>
        <v-btn
          x-small
          :loading='savingLine'
          color='success'
          @click='saveLine'
        >
        <template v-slot:loader>
          <v-progress-circular color='white' indeterminate :size='14' :width='1'></v-progress-circular>
        </template>
          <v-icon small>mdi-check</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-tooltip top>
          <template v-slot:activator='{ on }'>
            <v-btn
              v-on='on'
              x-small
              color='error'
            >
              <v-icon small>mdi-alert-circle</v-icon>
            </v-btn>
          </template>
          <span>Error in account, GST or Amount</span>
        </v-tooltip>
      </template>
    </td>
  </tr>
  <tr v-else>
    <td v-if='line.division'>{{ line.division_obj.division }}</td>
    <td v-else class='grey--text'>(none)</td>
    <td>{{ line.description }}</td>
    <td v-if='line.account'>{{ line.account_obj.name }}</td>
    <td v-else>(not set)</td>
    <td class='text-right'>{{ line.amount | dec }}</td>
    <td class='text-right'>{{ line.gst | dec }}</td>
    <td class='text-right'>{{ line_total | dec }}</td>
    <td>
      <wc-line-actions
        v-if='!disableEdit && !is_posted'
        hideSortUp
        hideSortDown
        @edit='setupEdit'
        @deleteLine="$emit('deleteLine', line)"
      >
      </wc-line-actions>
    </td>
  </tr>
</template>

<script>
import _ from 'lodash'
import formRules from '@/mixins/formRules'
import AccountPickerListItem from '@/modules/accounting/components/AccountPickerListItem'

export default {
  name: 'expense-line',
  mixins: [formRules],
  props: {
    line: {
      required: true
    },
    accounts: {
      required: true
    },
    disableEdit: {
      required: false,
      type: Boolean,
      default: true
    },
    editMode: {
      required: false,
      type: Boolean,
      default: false
    },
    is_posted: {
        required: false,
        type: Boolean,
        default: false
    }
  },
  data: () => ({
    savingLine: false,
    isGSTValid: false,
    isAmountValid: false,
    isFormValid: false,
    editLine: false,
    lineCopy: {}
  }),
  watch: {
    editMode () {
      if (this.editLine) {
        this.setupEdit()
      }
    }
  },
  computed: {
    lineCopy_amount () {
      return this.lineCopy.amount
    },
    line_total () {
      if (this.editLine) {
        if (this.lineCopy.gst) {
          return _.add(parseFloat(this.lineCopy.amount), parseFloat(this.lineCopy.gst))
        }
        return parseFloat(this.lineCopy.amount)
      } else if (this.line.gst) {
        return _.add(parseFloat(this.line.amount), parseFloat(this.line.gst))
      }
      return parseFloat(this.line.amount)
    }
  },
  methods: {
    cancel () {
      this.savingLine = false;
      this.editLine = false;
      this.$emit('cancelEdit');
    },
    async setupEdit () {
      this.lineCopy = _.cloneDeep(this.line);
      this.editLine = true;
      this.$nextTick(() => {
        this.$refs.form.resetValidation();
      })
      this.$nextTick(() => {
        this.$refs.div.focus();
      })
    },
    saveLine () {
      this.savingLine = true;
      if (this.lineCopy.gst == '') {
        this.lineCopy.gst = null;
      }
      this.$emit('save', this.lineCopy);
      this.editLine = false;
      this.savingLine = false;
    },
    calculateLineTotal() {
      if (this.editLine) {
        return _.sum(this.lineCopy.amount, this.lineCopy.gst);
      }
      return _.sum(this.line.amount, this.line.gst);
    },
    clearDivision () {
      this.lineCopy.division = null;
    },
    setInitialGST () {
      if ((this.lineCopy.gst == null || this.lineCopy.gst == '') && this.lineCopy.amount != null) {
        let gst = this.$store.state.misc.settings.tax_rate_perc * this.lineCopy.amount;
        this.lineCopy.gst = parseFloat(gst).toFixed(2);
      }
    }
  },
  components: {
    'account-picker-list-item': AccountPickerListItem
  }
}
</script>
